import React, { useState, useEffect, useMemo, useRef } from 'react';
import { formatNumberPrecise } from '../Utils';
// All data now comes from MongoDB (zoneserver): the agencies list, live host
// monthlyStats, and past cycle archives — merged by agencyId. (Was Firestore
// `agencies` collection + an admins-doc access check; host/archive stats already
// came from /api/salary.)
import { apiGet } from '../api';
import jsPDF from 'jspdf';

const videoSalaryTiers = [
    {target: 100000000, basic: 6000, bonus: 4000}, {target: 80000000, basic: 5000, bonus: 3000},
    {target: 60000000, basic: 4000, bonus: 2000}, {target: 40000000, basic: 3000, bonus: 1000},
    {target: 20000000, basic: 1200, bonus: 800}, {target: 10000000, basic: 600, bonus: 400},
    {target: 8000000, basic: 480, bonus: 320}, {target: 6500000, basic: 390, bonus: 260},
    {target: 4000000, basic: 240, bonus: 160}, {target: 3000000, basic: 180, bonus: 120},
    {target: 2000000, basic: 120, bonus: 80}, {target: 1000000, basic: 60, bonus: 40},
    {target: 500000, basic: 30, bonus: 20}, {target: 300000, basic: 19, bonus: 11},
];
const audioSalaryTiers = [
    {target: 100000000, basic: 6000, bonus: 2000}, {target: 80000000, basic: 5000, bonus: 1400},
    {target: 60000000, basic: 4000, bonus: 800}, {target: 40000000, basic: 2600, bonus: 600},
    {target: 20000000, basic: 1200, bonus: 400}, {target: 10000000, basic: 600, bonus: 200},
    {target: 8000000, basic: 480, bonus: 160}, {target: 6500000, basic: 390, bonus: 130},
    {target: 4000000, basic: 240, bonus: 80}, {target: 3000000, basic: 180, bonus: 60},
    {target: 2000000, basic: 120, bonus: 30}, {target: 1000000, basic: 65, bonus: 15},
    {target: 500000, basic: 30, bonus: 10}, {target: 300000, basic: 16, bonus: 8},
];
const agencyCommissionTiers = [
    {target: 100000000, commission: 1500}, {target: 80000000, commission: 1200},
    {target: 60000000, commission: 900}, {target: 40000000, commission: 600},
    {target: 20000000, commission: 300}, {target: 10000000, commission: 150},
    {target: 8000000, commission: 120}, {target: 6500000, commission: 97.5},
    {target: 4000000, commission: 60}, {target: 3000000, commission: 45},
    {target: 2000000, commission: 30}, {target: 1000000, commission: 15},
    {target: 500000, commission: 7.50}, {target: 300000, commission: 2.22},
];

const calculateHostSalary = (gold, liveDays, liveHours, hostType) => {
    const salaryTiers = hostType === "video" ? videoSalaryTiers : audioSalaryTiers;
    let basicSalary = 0, timeBonus = 0;
    for (const tier of salaryTiers) {
        if (gold >= tier.target) {
            basicSalary = tier.basic;
            if (liveDays >= 12 && liveHours >= 16) timeBonus = tier.bonus;
            break;
        }
    }
    return { basicSalary, timeBonus, totalSalary: basicSalary + timeBonus };
};

const calculateAgencyCommission = (gold) => {
    for (const tier of agencyCommissionTiers) { if (gold >= tier.target) return tier.commission; }
    return 0;
};

// Diamond is a separate, richer payout rate than cash — not the same value
// re-denominated. Host cash salary and agency cash commission are both driven
// by the tiers above at a flat 900tk/lakh and 135tk/lakh (Gold) respectively
// (1$=90tk); Diamond pays a flat 1100tk/lakh for everyone, host and agency alike.
// Rather than re-deriving "lakh of Gold" from scratch (which would have to
// reimplement the tiers' basic/bonus split and day/hour gating), each rate
// rescales the already-tier-correct $ salary/commission figure by how much
// richer the Diamond rate is than the cash rate it replaces.
const REAL_DIAMOND_PER_DOLLAR = 8183; // $1's real Diamond value
const HOST_CASH_TAKA_PER_LAKH = 900;
const HOST_DIAMOND_TAKA_PER_LAKH = 1100;
const AGENCY_CASH_TAKA_PER_LAKH = 135; // 1.5$/lakh × 90tk/$
const AGENCY_DIAMOND_TAKA_PER_LAKH = 1100;
const toHostDiamonds = (dollars) =>
    Math.round((dollars || 0) * REAL_DIAMOND_PER_DOLLAR * (HOST_DIAMOND_TAKA_PER_LAKH / HOST_CASH_TAKA_PER_LAKH));
const toAgencyDiamonds = (dollars) =>
    Math.round((dollars || 0) * REAL_DIAMOND_PER_DOLLAR * (AGENCY_DIAMOND_TAKA_PER_LAKH / AGENCY_CASH_TAKA_PER_LAKH));
const AGENCIES_PER_LOAD = 5;
const MasterSalarySheet = () => {
    const [allArchives, setAllArchives] = useState({});
    const [allLiveHosts, setAllLiveHosts] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState("current");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [visibleCount, setVisibleCount] = useState(AGENCIES_PER_LOAD);
    const [focusedRow, setFocusedRow] = useState(null);
    const [printingAgencyId, setPrintingAgencyId] = useState(null);
    const rowRefs = useRef({});

    // Load everything once from the backend, then page through agencies
    // client-side (was Firestore cursor pagination + per-batch /api/salary calls).
    // The admin-only routes 403 for non-admins, so no separate access check needed.
    useEffect(() => {
        let alive = true;
        const load = async () => {
            setLoading(true); setError(null);
            try {
                const [agencyList, hosts, archives] = await Promise.all([
                    apiGet('/api/admin/accounts/agencies'),
                    apiGet('/api/salary/hosts'),
                    apiGet('/api/salary/archives'),
                ]);
                if (!alive) return;
                const sorted = (Array.isArray(agencyList) ? agencyList : [])
                    .map(a => ({ ...a, uid: a.id }))
                    .sort((x, y) => (x.ID || 0) - (y.ID || 0));
                const arch = {};
                (Array.isArray(archives) ? archives : []).forEach(a => {
                    if (a.agencyId == null) return;
                    (arch[a.agencyId] = arch[a.agencyId] || []).push({ id: a.cycleId, ...a });
                });
                setAgencies(sorted);
                setAllLiveHosts(Array.isArray(hosts) ? hosts : []);
                setAllArchives(arch);
            } catch (err) { if (alive) setError(err.message || 'Failed to load salary data.'); }
            finally { if (alive) setLoading(false); }
        };
        load();
        return () => { alive = false; };
    }, []);

    const availablePeriods = useMemo(() => {
        const periods = new Set(['current']);
        Object.values(allArchives).flat().forEach(a => periods.add(a.id));
        return Array.from(periods).sort((a, b) => a === 'current' ? -1 : b === 'current' ? 1 : b.localeCompare(a));
    }, [allArchives]);

    // Index the live hosts by agencyId once. Grouping used to re-scan all ~7.5k
    // host rows for each agency; now that it runs over the whole agency list
    // rather than a five-row slice that would be ~1.6M comparisons per render.
    const hostsByAgency = useMemo(() => {
        const map = new Map();
        allLiveHosts.forEach(h => {
            if (h.agencyId == null) return;
            // Keyed as a string on purpose. The id is numeric in the database
            // but has travelled as a string through more than one schema over
            // the years, and a strict === between "100132" and 100132 silently
            // empties this whole table.
            const k = String(h.agencyId);
            const list = map.get(k);
            if (list) list.push(h); else map.set(k, [h]);
        });
        return map;
    }, [allLiveHosts]);

    // Group EVERY agency, drop the ones with nothing to report, then sort by
    // gold. Pagination slices AFTER all of that (see visibleGroups). It used to
    // slice first, off the agency list ordered by ID, so the page showed
    // whatever the five lowest agency IDs happened to hold — and almost every
    // agency is dormant: of 215 only ~11 earn in a given cycle, the first of
    // them sitting at position 56. So the sheet opened completely blank and
    // "Load More" paged through dead agencies five at a time, 43 clicks to
    // reach the end. Sorting was also computed inside the slice, so the order
    // reshuffled every time more was loaded.
    const groupedData = useMemo(() => {
        if (agencies.length === 0) return [];
        const data = agencies.map(agency => {
            let hostRecords = [], totals = { totalGold: 0, totalSalary: 0, totalCommission: 0 };
            const isLive = selectedPeriod === 'current';
            if (isLive) {
                hostRecords = (hostsByAgency.get(String(agency.ID)) || []).slice();
                hostRecords.sort((a, b) => (b.monthlyStats?.totalGold || 0) - (a.monthlyStats?.totalGold || 0));
                if (hostRecords.length > 0) {
                    totals = hostRecords.reduce((acc, host) => {
                        const stats = host.monthlyStats || {};
                        const gold = stats.totalGold || 0;
                        const salaryInfo = calculateHostSalary(gold, stats.validLiveDays || 0, stats.totalLiveHours || 0, host.hostType || 'audio');
                        acc.totalGold += gold; acc.totalSalary += salaryInfo.totalSalary; acc.totalCommission += calculateAgencyCommission(gold);
                        return acc;
                    }, { totalGold: 0, totalSalary: 0, totalCommission: 0 });
                }
            } else {
                // Archives are keyed two different ways and BOTH piles have to
                // be searched — not just whichever key happens to exist. Rows
                // migrated from Firestore carry the agency's Firebase uid in
                // agencyId; runArchiveAndReset, which took over from the 21st,
                // writes the numeric ID instead. So any agency with real history
                // has rows under both keys, and the old `uid || ID` stopped at
                // the migrated pile and never looked at a single Mongo-era
                // cycle: 2026-07 rendered 2 of its 11 agencies, and the 9 it
                // dropped were the biggest earners in the month.
                const archivesForAgency = [
                    ...(allArchives[agency.uid] || []),
                    ...(allArchives[agency.ID] || []),
                ];
                const periodArchive = archivesForAgency.find(a => a.id === selectedPeriod);
                if (periodArchive) {
                    hostRecords = [...(periodArchive.hostRecords || [])].sort((a, b) => (b.Gold || 0) - (a.Gold || 0));
                    totals = { totalGold: periodArchive.totalGold || 0, totalSalary: periodArchive.totalSalary || 0, totalCommission: periodArchive.totalCommission || 0 };
                }
            }
            return { agencyName: agency.name, agencyId: agency.ID, hostRecords, totals, isLive };
        }).filter(g => g.hostRecords.length > 0 && g.totals.totalGold > 0);
        data.sort((a, b) => b.totals.totalGold - a.totals.totalGold);
        return data;
    }, [agencies, hostsByAgency, allArchives, selectedPeriod]);

    const handleRowFocus = (rowId) => setFocusedRow(focusedRow === rowId ? null : rowId);
    // Pagination is client-side over the agencies that actually have something
    // to show, so the first page is never empty and the button really does mean
    // "there is more below".
    const visibleGroups = useMemo(() => groupedData.slice(0, visibleCount), [groupedData, visibleCount]);
    const hasMoreAgencies = visibleCount < groupedData.length;
    const handleLoadMore = () => setVisibleCount(c => c + AGENCIES_PER_LOAD);
    const handleLoadAll = () => setVisibleCount(groupedData.length);
    // Each period has its own set of earning agencies, so start a newly picked
    // one from the top instead of carrying over a count expanded for the last.
    useEffect(() => { setVisibleCount(AGENCIES_PER_LOAD); }, [selectedPeriod]);

    const toBase64 = file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = error => reject(error);
    });

    const drawTextWithEmoji = (pdf, text, x, y, options) => {
        const EMOJI_SPACING_CORRECTION = 0.4;
        const symbolClass = '[\\u2190-\\u21FF\\u2460-\\u24FF\\u2500-\\u25FF\\u2600-\\u26FF\\u2700-\\u27BF\\u2900-\\u297F\\u2B00-\\u2BFF\\u{1F100}-\\u{1F1FF}\\u{1F300}-\\u{1FAFF}]';
        const isSymbol = (seg) => new RegExp(`^(?:${symbolClass}|\\p{Emoji_Presentation}|\\p{Extended_Pictographic})`, 'u').test(seg);
        const segments = text.match(new RegExp(`(?:${symbolClass}|\\p{Emoji_Presentation}|\\p{Extended_Pictographic})+|.`, 'gu')) || [];
        let currentX = x;
        const fontSize = pdf.getFontSize();
        const scaleFactor = pdf.internal.scaleFactor;
        for (const segment of segments) {
            const emoji = isSymbol(segment);
            pdf.setFont(emoji ? 'NotoEmoji' : 'HindSiliguri', 'normal');
            let segmentWidth = pdf.getStringUnitWidth(segment) * fontSize / scaleFactor;
            if (emoji) segmentWidth *= EMOJI_SPACING_CORRECTION;
            if (options && options.maxWidth && (currentX + segmentWidth) > (x + options.maxWidth)) break;
            pdf.text(segment, currentX, y);
            currentX += segmentWidth;
        }
    };

    const handlePrint = async (agencyId, agencyName) => {
        setPrintingAgencyId(agencyId);
        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            try {
                const [fontResponse, emojiFontResponse] = await Promise.all([fetch('/fonts/HindSiliguri-Regular.ttf'), fetch('/fonts/NotoEmoji-Regular.ttf')]);
                const fontBlob = await fontResponse.blob();
                const emojiFontBlob = await emojiFontResponse.blob();
                const [fontBase64, emojiFontBase64] = await Promise.all([toBase64(fontBlob), toBase64(emojiFontBlob)]);
                pdf.addFileToVFS('HindSiliguri-Regular.ttf', fontBase64);
                pdf.addFont('HindSiliguri-Regular.ttf', 'HindSiliguri', 'normal');
                pdf.addFileToVFS('NotoEmoji-Regular.ttf', emojiFontBase64);
                pdf.addFont('NotoEmoji-Regular.ttf', 'NotoEmoji', 'normal');
            } catch (fontError) { console.error("Font loading failed", fontError); }
            pdf.setFont('HindSiliguri', 'normal');
            const margin = 15;
            const contentWidth = pageWidth - (margin * 2);
            const agencyData = groupedData.find(g => g.agencyId === agencyId);
            if (!agencyData) { setPrintingAgencyId(null); return; }
            pdf.setFillColor(41, 128, 185);
            pdf.rect(0, 0, pageWidth, 35, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(16);
            pdf.text(`GolpoStream  - ${agencyData.agencyName}`, pageWidth / 2, 12, { align: 'center' });
            pdf.setFontSize(10);
            pdf.text(`Agency ID: ${agencyData.agencyId}`, pageWidth / 2, 18, { align: 'center' });
            pdf.text(`Period: ${selectedPeriod === 'current' ? 'Current Month (Live)' : selectedPeriod}`, pageWidth / 2, 24, { align: 'center' });
            let yPosition = 45;
            pdf.setFontSize(14);
            pdf.setTextColor(0, 0, 0);
            pdf.text('SUMMARY', margin, yPosition);
            yPosition += 12;
            const summaryBoxes = [
                { label: 'Total Gold', value: String(formatNumberPrecise(agencyData.totals.totalGold)), color: [255, 193, 7] },
                { label: 'Total Salary', value: `$${agencyData.totals.totalSalary.toFixed(2)}`, sub: `${toHostDiamonds(agencyData.totals.totalSalary)} Diamonds`, color: [38, 166, 154] },
                { label: 'Agency Commission', value: `$${(agencyData.totals.totalCommission ?? 0).toFixed(2)}`, sub: `${toAgencyDiamonds(agencyData.totals.totalCommission ?? 0)} Diamonds`, color: [102, 187, 106] }
            ];
            const boxWidth = 55, boxHeight = 30, boxSpacing = 5;
            summaryBoxes.forEach((box, index) => {
                const xPos = margin + (index * (boxWidth + boxSpacing));
                pdf.setFillColor(box.color[0], box.color[1], box.color[2]);
                pdf.roundedRect(xPos, yPosition, boxWidth, boxHeight, 3, 3, 'F');
                pdf.setTextColor(255, 255, 255);
                pdf.setFontSize(9);
                pdf.text(box.label, xPos + boxWidth / 2, yPosition + 8, { align: 'center' });
                pdf.setFontSize(10);
                pdf.text(box.value, xPos + boxWidth / 2, yPosition + 17, { align: 'center' });
                if (box.sub) { pdf.setFontSize(8); pdf.text(box.sub, xPos + boxWidth / 2, yPosition + 25, { align: 'center' }); }
            });
            yPosition += boxHeight + 15;
            pdf.setFillColor(52, 152, 219);
            pdf.rect(margin, yPosition, contentWidth, 12, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(10);
            const headers = ['SL', 'Host Name', 'Host ID', 'Gold', 'Days', 'Hours', 'Basic', 'Bonus', 'Salary', 'Sal.Dia', 'Comm.', 'Com.Dia'];
            const columnWidths = [5, 37, 16, 16, 9, 10, 13, 13, 14, 17, 13, 17];
            let xPosition = margin + 2;
            headers.forEach((header, index) => { pdf.text(header, xPosition, yPosition + 8); xPosition += columnWidths[index]; });
            yPosition += 12;
            pdf.setFontSize(9);
            agencyData.hostRecords.forEach((record, index) => {
                if (yPosition > pageHeight - margin - 10) {
                    pdf.addPage(); yPosition = margin;
                    pdf.setFillColor(52, 152, 219); pdf.rect(margin, yPosition, contentWidth, 12, 'F');
                    pdf.setTextColor(255, 255, 255); pdf.setFontSize(10);
                    xPosition = margin + 2;
                    headers.forEach((header, idx) => { pdf.text(header, xPosition, yPosition + 8); xPosition += columnWidths[idx]; });
                    yPosition += 12; pdf.setFontSize(9);
                }
                const isLiveRow = agencyData.isLive;
                const stats = record.monthlyStats || {};
                const gold = isLiveRow ? (stats.totalGold ?? 0) : (record.Gold ?? 0);
                const days = isLiveRow ? (stats.validLiveDays ?? 0) : (record.days ?? 0);
                const hours = isLiveRow ? (stats.totalLiveHours ?? 0) : (record.hours ?? 0);
                const salaryInfo = calculateHostSalary(gold, days, hours, record.hostType || "audio");
                const finalRecord = {
                    basicSalary: isLiveRow ? salaryInfo.basicSalary : (record.basicSalary ?? 0),
                    timeBonus: isLiveRow ? salaryInfo.timeBonus : (record.timeBonus ?? 0),
                    totalSalary: isLiveRow ? salaryInfo.totalSalary : (record.totalSalary ?? 0)
                };
                const commission = calculateAgencyCommission(gold);
                if (index % 2 === 0) pdf.setFillColor(245, 245, 245); else pdf.setFillColor(255, 255, 255);
                pdf.rect(margin, yPosition, contentWidth, 10, 'F');
                pdf.setTextColor(0, 0, 0);
                const cellY = yPosition + 7;
                let currentX = margin + 2;
                pdf.setFont('HindSiliguri');
                pdf.text(String(index + 1), currentX, cellY); currentX += columnWidths[0];
                drawTextWithEmoji(pdf, String(record.username || 'N/A'), currentX, cellY, { maxWidth: columnWidths[1] - 2 }); currentX += columnWidths[1];
                pdf.setFont('HindSiliguri');
                pdf.text(String(isLiveRow ? record.ID : record.userId), currentX, cellY, { maxWidth: columnWidths[2] - 2 }); currentX += columnWidths[2];
                [formatNumberPrecise(gold), String(days), hours.toFixed(1), `$${finalRecord.basicSalary.toFixed(2)}`, `$${finalRecord.timeBonus.toFixed(2)}`, `$${finalRecord.totalSalary.toFixed(2)}`, String(toHostDiamonds(finalRecord.totalSalary)), `$${(commission ?? 0).toFixed(2)}`, String(toAgencyDiamonds(commission ?? 0))].forEach((data, i) => {
                    pdf.text(String(data), currentX, cellY, { maxWidth: columnWidths[i + 3] - 2 }); currentX += columnWidths[i + 3];
                });
                pdf.setDrawColor(200, 200, 200);
                pdf.line(margin, yPosition + 10, margin + contentWidth, yPosition + 10);
                yPosition += 10;
            });
            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i); pdf.setFontSize(8); pdf.setTextColor(100, 100, 100);
                pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
                pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
            }
            pdf.save(`${agencyName.replace(/[^a-zA-Z0-9]/g, '_')}_${selectedPeriod}_report.pdf`);
        } catch (err) { console.error("Failed to generate PDF", err); alert('PDF generation failed.'); }
        finally { setPrintingAgencyId(null); }
    };

    if (loading) return <div className="dark-loading"><div className="spinner" /><p>Loading Salary Data...</p></div>;
    if (error) return <div className="dark-error"><i className="fas fa-exclamation-circle" style={{ marginRight: 8 }} />{error}</div>;

    return (
        <div className="dark-page">
            <h2 style={{ textAlign: 'center', marginBottom: 16, background: 'linear-gradient(135deg,#ff4d6d,#c084fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Master Salary Sheet
            </h2>

            {/* Period Selector */}
            <div style={{ maxWidth: 400, margin: '0 auto 24px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16 }}>
                <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>Select Period:</label>
                <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#f1f5f9', fontSize: 14 }}>
                    {availablePeriods.map(period => (
                        <option key={period} value={period} style={{ background: '#1a0a2e' }}>
                            {period === 'current' ? 'Current Month (Live)' : `Report - ${period}`}
                        </option>
                    ))}
                </select>
            </div>

            {groupedData.length === 0 && !loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.3)' }}>No data available.</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    {visibleGroups.map(group => (
                        <div key={group.agencyId} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden' }}>
                            {/* Agency Header */}
                            <div style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#fff' }}>{group.agencyName}</h3>
                                    <p style={{ margin: '4px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Agency ID: {group.agencyId}</p>
                                </div>
                                <button onClick={() => handlePrint(group.agencyId, group.agencyName)} disabled={printingAgencyId === group.agencyId} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: printingAgencyId === group.agencyId ? 0.6 : 1 }}>
                                    {printingAgencyId === group.agencyId ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Printing...</> : <><i className="fas fa-print" style={{ marginRight: 6 }} />Print PDF</>}
                                </button>
                            </div>

                            {/* Summary Cards */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, padding: 16 }}>
                                {[
                                    { label: `Total Gold ${group.isLive ? '(Live)' : '(Final)'}`, value: formatNumberPrecise(group.totals.totalGold), color: '#f59e0b' },
                                    { label: `Total Salary ${group.isLive ? '(Live)' : '(Final)'}`, value: `$${group.totals.totalSalary.toFixed(2)}`, sub: `${toHostDiamonds(group.totals.totalSalary)} 💎`, color: '#2dd4bf' },
                                    { label: `Agency Comm. ${group.isLive ? '(Live)' : '(Final)'}`, value: `$${(group.totals.totalCommission ?? 0).toFixed(2)}`, sub: `${toAgencyDiamonds(group.totals.totalCommission ?? 0)} 💎`, color: '#4ade80' },
                                ].map((s, i) => (
                                    <div key={i} className="dark-card" style={{ borderTop: `3px solid ${s.color}`, textAlign: 'center' }}>
                                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>{s.label}</p>
                                        <p style={{ fontSize: 18, fontWeight: 700, color: s.color, margin: '0 0 4px' }}>{s.value}</p>
                                        {s.sub && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: 0 }}>{s.sub}</p>}
                                    </div>
                                ))}
                            </div>

                            {/* Host Table */}
                            <div style={{ overflowX: 'auto', padding: '0 16px 16px' }}>
                                <table className="dark-table" style={{ minWidth: 1100 }}>
                                    <thead>
                                        <tr>
                                            {['SL', 'Host Name', 'Host ID', 'Gold', 'Days', 'Hours', 'Basic ($)', 'Bonus ($)', 'Total Salary ($)', 'Salary (Dia)', 'Agency Comm. ($)', 'Comm. (Dia)'].map(h => (
                                                <th key={h} style={{ textAlign: h === 'SL' || h === 'Host Name' || h === 'Host ID' ? 'left' : 'right', whiteSpace: 'nowrap' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.hostRecords.map((record, index) => {
                                            const isLiveRow = group.isLive;
                                            const stats = record.monthlyStats || {};
                                            const gold = isLiveRow ? (stats.totalGold ?? 0) : (record.Gold ?? 0);
                                            const days = isLiveRow ? (stats.validLiveDays ?? 0) : (record.days ?? 0);
                                            const hours = isLiveRow ? (stats.totalLiveHours ?? 0) : (record.hours ?? 0);
                                            const salaryInfo = calculateHostSalary(gold, days, hours, record.hostType || "audio");
                                            const finalRecord = {
                                                basicSalary: isLiveRow ? salaryInfo.basicSalary : (record.basicSalary ?? 0),
                                                timeBonus: isLiveRow ? salaryInfo.timeBonus : (record.timeBonus ?? 0),
                                                totalSalary: isLiveRow ? salaryInfo.totalSalary : (record.totalSalary ?? 0)
                                            };
                                            const commission = calculateAgencyCommission(gold);
                                            const rowId = isLiveRow ? record.id : record.userId;
                                            const isFocused = focusedRow === rowId;
                                            return (
                                                <tr key={rowId} ref={el => rowRefs.current[rowId] = el} onClick={() => handleRowFocus(rowId)} style={{ cursor: 'pointer', background: isFocused ? 'rgba(167,139,250,0.15)' : undefined, outline: isFocused ? '2px solid rgba(167,139,250,0.5)' : undefined }}>
                                                    <td style={{ color: 'rgba(255,255,255,0.4)' }}>{index + 1}</td>
                                                    <td className="font-medium" style={{ color: isFocused ? '#c084fc' : undefined }}>{record.username}</td>
                                                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{isLiveRow ? record.ID : record.userId}</td>
                                                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#f59e0b' }}>{formatNumberPrecise(gold)}</td>
                                                    <td style={{ textAlign: 'right', color: '#60a5fa' }}>{days}</td>
                                                    <td style={{ textAlign: 'right', color: '#22d3ee' }}>{hours.toFixed(1)}</td>
                                                    <td style={{ textAlign: 'right', color: '#34d399' }}>${finalRecord.basicSalary.toFixed(2)}</td>
                                                    <td style={{ textAlign: 'right', color: '#2dd4bf' }}>${finalRecord.timeBonus.toFixed(2)}</td>
                                                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#4ade80' }}>${finalRecord.totalSalary.toFixed(2)}</td>
                                                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#f472b6' }}>{toHostDiamonds(finalRecord.totalSalary)}</td>
                                                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#60a5fa' }}>${(commission ?? 0).toFixed(2)}</td>
                                                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#c084fc' }}>{toAgencyDiamonds(commission ?? 0)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}

                    {/* Load More */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 12, paddingTop: 8 }}>
                        {hasMoreAgencies && (
                            <>
                                <button onClick={handleLoadMore} className="btn-upload" style={{ padding: '10px 28px' }}>
                                    Load More
                                </button>
                                <button onClick={handleLoadAll} className="btn-upload" style={{ padding: '10px 28px', background: 'linear-gradient(135deg,#7c3aed,#ec4899)' }}>
                                    Load All
                                </button>
                            </>
                        )}
                        {!loading && !hasMoreAgencies && groupedData.length > 0 && (
                            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>All agencies have been loaded.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MasterSalarySheet;
